import { ChangeDetectionStrategy, Component, inject, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  ChurnFlowProductsBaseService,
  ChurnFlowReasonInfoboxService,
} from '@core/churn-flow-logic';
import { ChurnFlowSolutionCardI, ChurnReason } from '@core/churn-flow-types';
import {
  ChurnFlowReasonTemplateComponent,
  ChurnFlowSolutionCardComponent,
  ChurnFlowStepTemplateComponent,
} from '@core/churn-flow-ui';
import { TranslatePipe } from '@core/i18n-legacy';
import { ButtonComponent, IconComponent } from '@core/user-interface-legacy';

import { ChurnFlowInsightsComponent } from '../../components/churn-flow-insights/churn-flow-insights.component';
import { ChurnFlowReasonBaseComponent } from '../../components/churn-flow-reason-base.component';

type PromotedAddons = 'performance_management' | 'whatsapp_hiring' | 'journeys';

@Component({
  selector: 'tlc-churn-moving-to-another-tool-page',
  templateUrl: './churn-moving-to-another-tool-page.component.html',
  styleUrls: ['./churn-moving-to-another-tool-page.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    ChurnFlowInsightsComponent,
    ChurnFlowReasonTemplateComponent,
    ChurnFlowSolutionCardComponent,
    ChurnFlowStepTemplateComponent,
    IconComponent,
    TranslatePipe,
  ],
})
export class ChurnMovingToAnotherToolPageComponent extends ChurnFlowReasonBaseComponent {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly productsService = inject(ChurnFlowProductsBaseService);

  protected readonly reason = ChurnReason.MovingToAnotherTool;

  public readonly isTellentHrActive = this.productsService.isTellentHrActive;
  public readonly isGrowActive = this.productsService.isGrowActive;
  public readonly isWhatsAppHiringActive = this.productsService.isWhatsAppHiringActive;
  public readonly areJourneysActive = this.productsService.areJourneysActive;

  public readonly infoboxMessage: Signal<string | undefined> = inject(
    ChurnFlowReasonInfoboxService,
  ).getReasonInfoboxMessage(ChurnReason.MovingToAnotherTool);

  public readonly hris: ChurnFlowSolutionCardI = {
    iconVariant: 'tellent-hr',
    title: this.t('tlc.churn_flow.steps.moving_to_another_tool.solutions.hris.title'),
    incentives: [
      this.t('tlc.churn_flow.steps.moving_to_another_tool.solutions.hris.incentive_1'),
      this.t('tlc.churn_flow.steps.moving_to_another_tool.solutions.hris.incentive_2'),
    ],
    ctaText: this.isTellentHrActive()
      ? this.t('tlc.churn_flow.steps.not_hiring.solutions.add_hris.cta_alternate')
      : this.t('tlc.churn_flow.steps.moving_to_another_tool.solutions.hris.cta'),
  };

  public readonly performanceManagement: ChurnFlowSolutionCardI = {
    iconVariant: 'performance-management',
    title: this.t(
      'tlc.churn_flow.steps.moving_to_another_tool.solutions.performance_management.title',
    ),
    incentives: [
      this.t(
        'tlc.churn_flow.steps.moving_to_another_tool.solutions.performance_management.incentive_1',
      ),
      this.t(
        'tlc.churn_flow.steps.moving_to_another_tool.solutions.performance_management.incentive_2',
      ),
    ],
    ctaText: this.isGrowActive()
      ? this.t('tlc.churn_flow.common.already_activated')
      : this.t('tlc.churn_flow.steps.moving_to_another_tool.solutions.performance_management.cta'),
  };

  public readonly whatsappHiring: ChurnFlowSolutionCardI = {
    iconVariant: 'whatsapp-hiring',
    title: this.t('tlc.churn_flow.steps.moving_to_another_tool.solutions.whatsapp_hiring.title'),
    incentives: [
      this.t('tlc.churn_flow.steps.moving_to_another_tool.solutions.whatsapp_hiring.incentive_1'),
      this.t('tlc.churn_flow.steps.moving_to_another_tool.solutions.whatsapp_hiring.incentive_2'),
    ],
    ctaText: this.isWhatsAppHiringActive()
      ? this.t('tlc.churn_flow.common.already_activated')
      : this.t('tlc.churn_flow.steps.moving_to_another_tool.solutions.whatsapp_hiring.cta'),
  };

  public readonly journeys: ChurnFlowSolutionCardI = {
    iconVariant: 'workflow-automation',
    title: this.t('tlc.churn_flow.steps.moving_to_another_tool.solutions.journeys.title'),
    incentives: [
      this.t('tlc.churn_flow.steps.moving_to_another_tool.solutions.journeys.incentive_1'),
      this.t('tlc.churn_flow.steps.moving_to_another_tool.solutions.journeys.incentive_2'),
    ],
    ctaText: this.areJourneysActive()
      ? this.t('tlc.churn_flow.common.already_activated')
      : this.t('tlc.churn_flow.steps.moving_to_another_tool.solutions.journeys.cta'),
  };

  public activateHrTrial(): void {
    this.router
      .navigate([{ outlets: { dialog: null, primary: ['billing'] } }])
      .then(() => this.churnFlowDataService.activateTellentHrFreeTrial());
  }

  public askQuestionAboutAddon(addon: PromotedAddons): void {
    const messages: Record<PromotedAddons, string> = {
      ['performance_management']: this.t(
        'tlc.churn_flow.steps.moving_to_another_tool.solutions.performance_management.intercom_message',
      ),
      ['whatsapp_hiring']: this.t(
        'tlc.churn_flow.steps.moving_to_another_tool.solutions.whatsapp_hiring.intercom_message',
      ),
      ['journeys']: this.t(
        'tlc.churn_flow.steps.moving_to_another_tool.solutions.journeys.intercom_message',
      ),
    };

    // We are adding a query parameter to help CS team identify the context of the message and redirect user to a proper Intercom path
    this.router
      .navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { ['question_about_addon']: true },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .then(() => this.openIntercom(messages[addon]));
  }
}
